import React, { useState, useEffect, useRef, useMemo } from 'react';
import { calendrierAPI, evenementsAPI, exportAPI, brandingAPI } from '../services/api';
import { couleurEvenement } from '../services/evenementsCategories';
import { peutModifier } from '../services/droits';
import { chargerImagePourPdf } from '../services/planningPdfCommun';
import { genererPdfEvenements } from '../services/planningPdfEvenements';
import PlanningTopbar from './PlanningTopbar';
import ModalePlanning from './ModalePlanning';
import PlanningAnnuelEnvoi from './PlanningAnnuelEnvoi';
import './PlanningApp.css';

/**
 * ONGLET « EXPORT PDF ÉVÉNEMENTS » (lot 5.7) — vue `#view-exportevt` de la
 * maquette, et DERNIER écran du module Planning annuel.
 *
 * IL REMPLACE `ExportDates.js`, SUPPRIMÉ DANS LE MÊME COMMIT. L'ancienne modale
 * de 750 px produisait un tableau sans jamais rien montrer avant de télécharger,
 * portait sa propre copie des huit catégories — huit emojis figés dans son code,
 * que renommer une catégorie au Paramétrage ne changeait pas — et appelait
 * `alert()` quand la sélection était vide. C'est le dernier endroit du module qui
 * ignorait le référentiel : l'écart **E17** se referme ici.
 *
 * ── LE MÊME ESPRIT QUE L'EXPORT CALENDRIER (demande de Bertrand, 2026-08-12) ──
 * Tout ce qui fait la parenté des deux écrans est PARTAGÉ, pas recopié :
 *   · les DEUX LOGOS, l'en-tête et le pied  -> `services/planningPdfCommun.js`
 *   · « Enregistrer le modèle » distinct de « Générer le PDF » (écart E27)
 *   · la carte « Modèles d'export enregistrés » et ses quatre gestes
 *   · l'indicateur « planning modifié depuis », adossé à une empreinte du contenu
 *   · l'aperçu qui EST le PDF, et son zoom d'ouverture calculé (écart E21)
 *   · les mêmes routes `/api/export/modeles`, avec `type = 'evenements'` — valeur
 *     que la migration v37 avait déjà déclarée dans son CHECK. Aucune migration.
 *
 * ── CE QUI LUI EST PROPRE ────────────────────────────────────────────────────
 * L'orientation (A4 **portrait par défaut**, paysage sur 2 colonnes) et le
 * regroupement par mois. Et la sélection des événements est ici une simple liste
 * à cocher sur deux colonnes, dans la carte de gauche, là où le calendrier ouvre
 * des panneaux flottants : ~33 cases simples ne sont pas une matrice de 192, et
 * la maquette les pose en clair. Un panneau flottant aurait été de la mécanique
 * inventée pour ressembler à l'écran voisin.
 */

/* ═══ LE ZOOM D'AJUSTEMENT SE CALCULE, IL NE SE DEVINE PAS ═══════════════════
   Leçon de la 2ᵉ recette du lot 5.6 : « à 85 % on ne voit pas toute la page ».
   Un pourcentage porte sur la TAILLE RÉELLE et ne dit rien du cadre où la page
   s'affiche. À 96 points par pouce, une A4 portrait fait 794 × 1123 pixels CSS ;
   le facteur qui la fait tenir se déduit donc des dimensions mesurées du cadre.
   Ici l'orientation est réglable : les deux formats sont donc tabulés. */
const PAGE_PX = {
    portrait: { largeur: 210 / 25.4 * 96, hauteur: 297 / 25.4 * 96 },
    paysage: { largeur: 297 / 25.4 * 96, hauteur: 210 / 25.4 * 96 }
};
const MARGE_VISIONNEUSE = 26;    // barres de défilement + liseré de la visionneuse

function zoomDAjustement(cadre, orientation) {
    const page = PAGE_PX[orientation] || PAGE_PX.portrait;
    if (!cadre) return 85;
    const l = cadre.clientWidth - MARGE_VISIONNEUSE;
    const h = cadre.clientHeight - MARGE_VISIONNEUSE;
    if (l <= 0 || h <= 0) return 85;
    return Math.max(20, Math.min(200, Math.floor(Math.min(l / page.largeur, h / page.hauteur) * 100)));
}

function PlanningExportEvenements({ user, config, onglet, onChangeOnglet, anneeId, modeleDemande, onModeleDemandeTraite }) {
    const [chargement, setChargement] = useState(true);
    const [erreur, setErreur] = useState(null);
    const [anneeScolaire, setAnneeScolaire] = useState(null);
    const [evenements, setEvenements] = useState([]);
    const [logo, setLogo] = useState(null);
    const [logoUniversite, setLogoUniversite] = useState(null);

    // Réglages. `evtEteints` est une LISTE D'EXCEPTIONS : tout est inclus par
    // défaut, on ne mémorise que ce qu'on retire. Un événement créé demain entre
    // donc dans le document sans que personne ait à le cocher — même principe que
    // les matrices de l'export calendrier.
    const [titre, setTitre] = useState('');
    const [orientation, setOrientation] = useState('portrait');
    const [grouperParMois, setGrouperParMois] = useState(false);
    const [evtEteints, setEvtEteints] = useState(() => new Set());

    const [zoom, setZoom] = useState(null);
    const [ajustement, setAjustement] = useState(85);
    const cadreRef = useRef(null);
    const [apercu, setApercu] = useState(null);      // { url, pages, evenements }
    const [enregistrement, setEnregistrement] = useState(false);
    const [information, setInformation] = useState(null);

    const [modeles, setModeles] = useState([]);
    const [modeleActif, setModeleActif] = useState(null);
    const [nouveauModele, setNouveauModele] = useState(null);   // { nom }
    // Lot PA.3 : le modèle dont la fenêtre « Envoyer » est ouverte
    const [envoi, setEnvoi] = useState(null);
    const [confirmation, setConfirmation] = useState(null);

    const anneeEffective = anneeId || config?.anneeScolaireId;
    const deptId = config?.departement?.id;

    // Enregistrer, remplacer et supprimer sont des écritures, refusées en 403 à
    // qui n'a que « consulter ». Le bouton est donc grisé AVEC SON MOTIF plutôt
    // que cliquable pour rien. Générer le PDF reste permis : c'est une lecture.
    const peutEcrire = peutModifier(user, deptId, 'planning');
    const motifLectureSeule = "Vous avez « consulter » sur le Planning : "
        + 'la génération du PDF reste possible, pas l\'enregistrement des réglages.';

    const retenus = useMemo(
        () => evenements.filter(e => !evtEteints.has(e.id)),
        [evenements, evtEteints]
    );

    /* ═══ CHARGEMENT ═════════════════════════════════════════════════════════ */
    useEffect(() => {
        let vivant = true;
        if (!deptId || !anneeEffective) { setChargement(false); return undefined; }

        (async () => {
            setChargement(true);
            setErreur(null);
            try {
                const [repCal, repEvt, repCat, repCfg, repMod, repBranding] = await Promise.all([
                    // Seul `anneeScolaire` sert ici — mais c'est la route que
                    // l'écran voisin emploie déjà, et la variante légère
                    // (`/calendrier/toutes?visibles=true`) omet une année masquée
                    // du sélecteur, dont l'export doit pourtant rester possible.
                    calendrierAPI.getByAnnee(anneeEffective),
                    evenementsAPI.getConfig(deptId, { anneeScolaireId: anneeEffective }),
                    evenementsAPI.getCategories(deptId),
                    exportAPI.getConfig(deptId, anneeEffective),
                    exportAPI.getModeles(deptId, anneeEffective, 'evenements'),
                    // Une identité visuelle absente ne doit pas emporter l'écran :
                    // le PDF sortira sans logo, pas en erreur.
                    brandingAPI.get().catch(() => ({ data: {} }))
                ]);
                if (!vivant) return;

                const annee = repCal.data.anneeScolaire || null;
                setAnneeScolaire(annee);
                setModeles(repMod.data.modeles || []);

                // Les événements arrivent RÉSOLUS : libellé, date, couleur du
                // référentiel. Le générateur ne saura rien des catégories — c'est
                // ce qui referme l'écart E17 pour ce dernier écran.
                const categories = repCat.data.categories || [];
                const evts = (repEvt.data.evenements || [])
                    .filter(e => e.date_debut)
                    .map(e => ({
                        id: e.id,
                        date: String(e.date_debut).split('T')[0],
                        libelle: e.libelle,
                        couleur: couleurEvenement(e, categories),
                        logoUrl: e.logo_url || null
                    }))
                    .sort((a, b) => (a.date < b.date ? -1 : 1));
                setEvenements(evts);

                amorcerReglages(repCfg.data.config || {}, annee);

                const [univ, dept] = await Promise.all([
                    chargerImagePourPdf(repBranding.data?.logo_universite),
                    chargerImagePourPdf(config.departement?.logo_url)
                ]);
                if (vivant) { setLogoUniversite(univ); setLogo(dept); }

                // L'image d'un événement est l'EXCEPTION (écart E15) : en pratique
                // aucune n'est renseignée, la boucle ne charge donc rien. Elle
                // existe parce que l'écart promet que cette image reste affichée
                // partout où elle l'est — le papier compris.
                const avecImage = evts.filter(e => e.logoUrl);
                if (avecImage.length) {
                    const images = await Promise.all(avecImage.map(e => chargerImagePourPdf(e.logoUrl)));
                    if (!vivant) return;
                    const parId = new Map(avecImage.map((e, i) => [e.id, images[i]]));
                    setEvenements(liste => liste.map(e => (
                        parId.has(e.id) ? { ...e, image: parId.get(e.id) } : e
                    )));
                }
            } catch (e) {
                if (vivant) {
                    setErreur("Les événements n'ont pas pu être chargés : "
                        + (e.response?.data?.error || e.message));
                }
            } finally {
                if (vivant) setChargement(false);
            }
        })();

        return () => { vivant = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [deptId, anneeEffective]);

    /**
     * AMORÇAGE — rien n'est à recocher, même en venant de l'ancienne modale.
     *
     * Deux sources, dans cet ordre :
     *   1. la clé `evenements` d'`export_config` : les réglages de CET écran ;
     *   2. à défaut, les clés `dates*` qu'`ExportDates.js` écrivait depuis des
     *      années (`datesTitre`, `datesEventSelections`). Un département qui avait
     *      décoché douze événements dans l'ancienne modale les retrouve décochés.
     *
     * `datesEventLabels` n'est PAS repris : le renommage de l'export calendrier
     * sert à faire tenir un libellé dans 3 mm de large, contrainte qui n'existe
     * pas sur une liste où chaque ligne fait toute la largeur. Ce document imprime
     * donc le libellé réel de l'événement (décision Bertrand du 2026-08-12).
     */
    const amorcerReglages = (sauvegarde, annee) => {
        const propre = sauvegarde.evenements;
        if (propre) {
            setTitre(propre.titre || titreParDefaut(annee));
            setOrientation(propre.orientation === 'paysage' ? 'paysage' : 'portrait');
            setGrouperParMois(!!propre.grouperParMois);
            setEvtEteints(new Set(propre.evtEteints || []));
            return;
        }

        setTitre(sauvegarde.datesTitre || titreParDefaut(annee));
        const anciennes = sauvegarde.datesEventSelections;
        if (anciennes) {
            setEvtEteints(new Set(
                Object.keys(anciennes)
                    .filter(id => anciennes[id] === false)
                    .map(id => parseInt(id, 10))
                    .filter(Number.isInteger)
            ));
        }
    };

    /** « Événements 2025-2026 » — le titre du dessin, avec la vraie année. */
    const titreParDefaut = (annee) =>
        'Événements' + ((annee || anneeScolaire)?.libelle ? ' ' + (annee || anneeScolaire).libelle : '');

    /* ═══ APERÇU — LE VRAI PDF ═══════════════════════════════════════════════
       Régénéré après une pause : cocher une case redessine le document, et le
       faire à chaque frappe du titre donnerait un écran qui saccade. */
    const urlPrecedente = useRef(null);
    useEffect(() => {
        if (chargement) return undefined;
        const minuteur = setTimeout(() => {
            try {
                const { pdf, pages, evenementsImprimes } = construirePdf();
                const url = URL.createObjectURL(pdf.output('blob'));
                if (urlPrecedente.current) URL.revokeObjectURL(urlPrecedente.current);
                urlPrecedente.current = url;
                setApercu({ url, pages, evenements: evenementsImprimes });
                setErreur(null);
            } catch (e) {
                setErreur("L'aperçu n'a pas pu être produit : " + e.message);
            }
        }, 350);
        return () => clearTimeout(minuteur);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chargement, evenements, evtEteints, orientation, grouperParMois, titre, logo, logoUniversite]);

    useEffect(() => () => {
        if (urlPrecedente.current) URL.revokeObjectURL(urlPrecedente.current);
    }, []);

    // Le cadre est mesuré à l'affichage, à chaque redimensionnement de la fenêtre
    // ET à chaque changement d'orientation — une page couchée ne tient pas au même
    // facteur qu'une page debout. Tant que l'utilisateur n'a pas touché au zoom,
    // l'aperçu reste ajusté ; dès qu'il l'a réglé, on ne lui reprend pas la main.
    useEffect(() => {
        if (chargement) return undefined;
        const mesurer = () => {
            const a = zoomDAjustement(cadreRef.current, orientation);
            setAjustement(a);
            setZoom(z => (z === null ? a : z));
        };
        mesurer();
        window.addEventListener('resize', mesurer);
        return () => window.removeEventListener('resize', mesurer);
    }, [chargement, orientation]);

    /* ═══ LES RÉGLAGES, DÉTACHÉS DE L'ÉCRAN ══════════════════════════════════
       `construirePdf()` sans argument dessine l'aperçu — les réglages courants.
       Avec un argument, il dessine CEUX D'UN MODÈLE, sans passer par l'état de
       React : « Télécharger » depuis la liste ne doit ni modifier l'écran, ni
       attendre un rendu. Poser les réglages dans l'état puis générer aussitôt
       produirait le PDF de l'état PRÉCÉDENT — un `setState` n'est pas lu dans la
       même passe. Décalage invisible en relecture, parfaitement visible dans le
       fichier obtenu (leçon du lot 5.6 bis). */
    const reglagesCourants = () => ({ titre, orientation, grouperParMois, evtEteints });

    const reglagesDepuisConfig = (cfg) => ({
        titre: cfg?.titre || '',
        orientation: cfg?.orientation === 'paysage' ? 'paysage' : 'portrait',
        grouperParMois: !!cfg?.grouperParMois,
        evtEteints: new Set(cfg?.evtEteints || [])
    });

    /** La forme envoyée au serveur — la même que celle relue à l'ouverture. */
    const configCourante = () => ({
        titre,
        orientation,
        grouperParMois,
        evtEteints: [...evtEteints]
    });

    const construirePdf = (reglages) => {
        const r = reglages || reglagesCourants();
        return genererPdfEvenements({
            evenements: evenements.filter(e => !r.evtEteints.has(e.id)),
            orientation: r.orientation,
            grouperParMois: r.grouperParMois,
            titre: r.titre,
            sousTitre: (config?.departement?.titre_planning || config?.departement?.nom || '')
                + (anneeScolaire?.libelle ? ' - Année universitaire ' + anneeScolaire.libelle : ''),
            auteur: [user?.prenom, user?.nom].filter(Boolean).join(' ') || user?.email || '',
            logo,
            logoUniversite
        });
    };

    /* ═══ SÉLECTION ══════════════════════════════════════════════════════════ */
    const basculer = (id) => setEvtEteints(prev => {
        const suivant = new Set(prev);
        if (suivant.has(id)) suivant.delete(id); else suivant.add(id);
        return suivant;
    });

    const tout = (allumer) => setEvtEteints(
        allumer ? new Set() : new Set(evenements.map(e => e.id))
    );

    const changerOrientation = (valeur) => {
        setOrientation(valeur);
        // Le zoom repart à l'ajustement : le facteur qui faisait tenir une page
        // debout laisse une page couchée à moitié hors du cadre.
        setZoom(null);
    };

    /* ═══ GÉNÉRATION ═════════════════════════════════════════════════════════ */
    const nomFichier = (pourTitre) => {
        const propre = (pourTitre || titre || 'Evenements').replace(/[\\/:*?"<>|]/g, '').replace(/\s+/g, '_');
        return 'Evenements_' + (config?.departement?.code || 'IUT') + '_' + propre + '.pdf';
    };

    const enregistrerReglages = async () => {
        const existante = (await exportAPI.getConfig(deptId, anneeEffective)).data.config || {};
        await exportAPI.saveConfig({
            departementId: deptId,
            anneeScolaireId: anneeEffective,
            // Les clés des autres écrans sont conservées : `export_config` porte un
            // seul objet pour tout le module, et l'écraser ferait perdre les
            // réglages de l'export calendrier.
            config: { ...existante, evenements: configCourante() }
        });
    };

    const generer = async () => {
        setEnregistrement(true);
        try {
            const { pdf } = construirePdf();
            pdf.save(nomFichier());
            if (peutEcrire) await enregistrerReglages();
            setInformation({
                titre: 'PDF généré',
                message: 'Le fichier « ' + nomFichier() + ' » a été téléchargé'
                    + (peutEcrire
                        ? ', et vos réglages sont mémorisés pour la prochaine ouverture de cet écran.'
                        : ". Vos réglages ne sont pas mémorisés : vous n'avez que « consulter » sur le Planning.")
            });
        } catch (e) {
            // Un enregistrement refusé remonte à l'écran : il ne finit pas dans la
            // console, où personne ne le lira (règle du lot 5.1).
            setErreur('Le PDF a été produit mais les réglages n\'ont pas pu être enregistrés : '
                + (e.response?.data?.error || e.message));
        } finally {
            setEnregistrement(false);
        }
    };

    /* ═══ LES MODÈLES — QUATRE GESTES ════════════════════════════════════════ */
    const echecModele = (e, quoi) => setErreur(quoi + ' : ' + (e.response?.data?.error || e.message));

    const appliquerModele = (m) => {
        const r = reglagesDepuisConfig(m.config);
        setTitre(r.titre || titreParDefaut());
        setOrientation(r.orientation);
        setGrouperParMois(r.grouperParMois);
        setEvtEteints(r.evtEteints);
        setZoom(null);
        setModeleActif(m.id);
    };

    const telechargerModele = (m) => {
        const r = reglagesDepuisConfig(m.config);
        try {
            const { pdf } = construirePdf(r);
            pdf.save(nomFichier(r.titre));
        } catch (e) {
            setErreur('Le PDF de ce modèle n\'a pas pu être produit : ' + e.message);
        }
    };

    const enregistrerModele = async (nom) => {
        setEnregistrement(true);
        try {
            const rep = await exportAPI.creerModele({
                departementId: deptId,
                anneeScolaireId: anneeEffective,
                type: 'evenements',
                titre: nom,
                config: configCourante(),
                // v94 (lot PA.2) : la case « sur l'accueil » de la fenêtre
                surAccueil: nouveauModele?.surAccueil === true
            });
            const cree = rep.data.modele;
            setModeles(liste => [cree, ...liste]);
            setModeleActif(cree.id);
            setNouveauModele(null);
            setInformation({
                titre: 'Modèle enregistré',
                message: 'Le modèle « ' + cree.titre + ' » est rangé et partagé avec le département. '
                    + 'Il garde vos réglages : le PDF, lui, sera refabriqué à chaque téléchargement.'
            });
        } catch (e) {
            // Un nom déjà pris n'est pas une faute, c'est un « Enregistrer » de trop
            // sur un modèle qu'on croyait mettre à jour : on propose le geste voulu.
            if (e.response?.status === 409 && e.response.data?.id) {
                const existant = modeles.find(m => m.id === e.response.data.id);
                setNouveauModele(null);
                setConfirmation({
                    titre: 'Ce nom est déjà pris',
                    icone: 'fa-triangle-exclamation',
                    message: 'Un modèle nommé « ' + nom + ' » existe déjà pour ce planning. '
                        + 'Voulez-vous le remplacer par les réglages actuels ?',
                    libelle: 'Remplacer',
                    action: () => (existant
                        ? remplacerModele(existant)
                        : Promise.resolve(setErreur('Le modèle « ' + nom + ' » existe déjà.')))
                });
            } else {
                echecModele(e, "Le modèle n'a pas pu être enregistré");
            }
        } finally {
            setEnregistrement(false);
        }
    };

    const remplacerModele = async (m) => {
        setEnregistrement(true);
        try {
            const rep = await exportAPI.remplacerModele(m.id, { config: configCourante() });
            const maj = rep.data.modele;
            setModeles(liste => liste.map(x => (x.id === maj.id ? maj : x)));
            setModeleActif(maj.id);
            setConfirmation(null);
            setInformation({
                titre: 'Modèle mis à jour',
                message: 'Le modèle « ' + maj.titre + ' » porte désormais les réglages affichés à l\'écran.'
            });
        } catch (e) {
            setConfirmation(null);
            echecModele(e, "Le modèle n'a pas pu être mis à jour");
        } finally {
            setEnregistrement(false);
        }
    };

    /* ═══ LOT PA.2 — « SUR L'ACCUEIL » (même geste que l'export calendrier) ═══ */
    const basculerAccueilModele = async (m) => {
        try {
            await exportAPI.basculerAccueil(m.id, deptId, !m.surAccueil);
            setModeles(liste => liste.map(x => (x.id === m.id ? { ...x, surAccueil: !m.surAccueil } : x)));
        } catch (e) {
            echecModele(e, "Le modèle n'a pas pu changer de place");
        }
    };

    // Une vignette de l'accueil demande CE modèle (et peut-être son PDF) : chargé
    // une fois l'écran prêt, logos compris.
    useEffect(() => {
        if (!modeleDemande || chargement) return;
        const m = modeles.find(x => x.id === modeleDemande.id);
        if (!m) {
            setErreur("Ce modèle n'existe plus : il a peut-être été supprimé depuis l'accueil d'un collègue.");
        } else {
            appliquerModele(m);
            if (modeleDemande.telecharger) telechargerModele(m);
            if (modeleDemande.envoyer) setEnvoi(m);
        }
        onModeleDemandeTraite?.();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [modeleDemande, chargement, modeles]);

    const supprimerModele = async (m) => {
        setEnregistrement(true);
        try {
            await exportAPI.supprimerModele(m.id);
            setModeles(liste => liste.filter(x => x.id !== m.id));
            setModeleActif(actif => (actif === m.id ? null : actif));
            setConfirmation(null);
        } catch (e) {
            setConfirmation(null);
            echecModele(e, "Le modèle n'a pas pu être supprimé");
        } finally {
            setEnregistrement(false);
        }
    };

    const demanderRemplacement = (m) => setConfirmation({
        titre: 'Remplacer ce modèle ?',
        icone: 'fa-rotate',
        message: 'Le modèle « ' + m.titre + ' » prendra les réglages affichés à l\'écran '
            + '(titre, orientation, regroupement, événements retenus). Ses réglages actuels seront perdus.',
        libelle: 'Remplacer',
        action: () => remplacerModele(m)
    });

    const demanderSuppression = (m) => setConfirmation({
        titre: 'Supprimer ce modèle ?',
        icone: 'fa-trash',
        message: 'Le modèle « ' + m.titre + ' » sera retiré de la liste, pour tout le département. '
            + 'Aucun document n\'est supprimé — il n\'y en a jamais eu : un modèle ne garde que des réglages.',
        libelle: 'Supprimer',
        danger: true,
        action: () => supprimerModele(m)
    });

    /** « 12/08/2026 14:05 » — jamais une date brute de la base à l'écran. */
    const dateCourte = (iso) => {
        if (!iso) return '';
        const d = new Date(iso);
        return Number.isNaN(d.getTime())
            ? ''
            : d.toLocaleDateString('fr-FR') + ' ' + d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    };

    /* ═══ RENDU ══════════════════════════════════════════════════════════════ */
    // Le zoom réellement appliqué : celui de l'utilisateur, sinon l'ajustement
    // calculé sur le cadre. Jamais `null` — un `#zoom=null` ne veut rien dire.
    const zoomEffectif = zoom || ajustement;

    return (
        <div className="pl-root">
            <div className="sticky-top" style={{ margin: 0, top: 0 }}>
                <PlanningTopbar
                    sousTitre={(config?.departement?.nom || '')
                        + (anneeScolaire?.libelle ? ` · ${anneeScolaire.libelle}` : '')}
                    onglet={onglet}
                    onChangeOnglet={onChangeOnglet}
                />
            </div>

            <div className="pl-content">
                <div className="pl-toolbar pl-exp-barre">
                    <h2>Export PDF Événements</h2>
                    <span className="pl-grow" />
                    {/* Deux boutons là où la maquette n'en porte qu'un, « Générer &
                        enregistrer » : écart E27, tranché au lot 5.6 bis et appliqué
                        ici sans le rediscuter. Sous la règle « configuration seule »,
                        sortir le PDF de la semaine ne doit pas créer un modèle de
                        plus dans la liste. */}
                    <button
                        className="pl-btn"
                        onClick={() => setNouveauModele({ nom: titre || 'Événements' })}
                        disabled={chargement || enregistrement || !peutEcrire}
                        title={peutEcrire
                            ? 'Garder ces réglages sous un nom, pour tout le département'
                            : motifLectureSeule}
                    >
                        <i className="fa-solid fa-floppy-disk" /> Enregistrer le modèle
                    </button>
                    <button
                        className="pl-btn pl-btn-primary"
                        onClick={generer}
                        disabled={chargement || enregistrement}
                    >
                        <i className="fa-solid fa-file-pdf" /> {enregistrement ? 'Génération…' : 'Générer le PDF'}
                    </button>
                </div>

                {erreur && (
                    <div className="pl-bandeau pl-bandeau-warn">
                        <i className="fa-solid fa-triangle-exclamation" /> {erreur}
                    </div>
                )}

                {chargement ? (
                    <div className="pl-exp-vide"><i className="fa-solid fa-spinner fa-spin" /> Chargement des événements…</div>
                ) : (
                <div className="pl-exp-2col">
                    {/* ══ OPTIONS ═══════════════════════════════════════════ */}
                    <div className="pl-exp-left">
                        <div className="pl-card">
                            <div className="pl-card-header">
                                <span className="ttl"><i className="fa-solid fa-sliders" /> Options</span>
                            </div>
                            <div className="pl-card-body">
                                <div className="pl-exp-sec" style={{ marginTop: 0 }}>Titre du PDF</div>
                                <input
                                    className="pl-f pl-exp-titre"
                                    value={titre}
                                    onChange={e => setTitre(e.target.value)}
                                    placeholder={titreParDefaut()}
                                    maxLength={250}
                                />

                                <div className="pl-exp-sec">Mise en page</div>
                                <label className="pl-exp-opt">
                                    Orientation
                                    <span className="pl-grow" />
                                    <select
                                        className="pl-f"
                                        value={orientation}
                                        onChange={e => changerOrientation(e.target.value)}
                                    >
                                        <option value="portrait">Portrait</option>
                                        <option value="paysage">Paysage</option>
                                    </select>
                                </label>
                                <label className="pl-exp-opt">
                                    <input
                                        type="checkbox"
                                        checked={grouperParMois}
                                        onChange={e => setGrouperParMois(e.target.checked)}
                                    />
                                    Grouper par mois
                                </label>

                                <div className="pl-exp-sec">Événements à inclure</div>
                                <div className="pl-exp-sousbarre" style={{ marginBottom: 6 }}>
                                    <button className="pl-btn pl-btn-sm" onClick={() => tout(true)}>Tout</button>
                                    <button className="pl-btn pl-btn-sm" onClick={() => tout(false)}>Aucun</button>
                                    <span className="pl-hint">{retenus.length}/{evenements.length}</span>
                                </div>

                                {evenements.length ? (
                                    <div className="pl-exp-evts">
                                        {evenements.map(e => (
                                            <label
                                                key={e.id}
                                                className="pl-exp-evt"
                                                title={e.libelle + ' — ' + e.date.slice(8, 10) + '/' + e.date.slice(5, 7)}
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={!evtEteints.has(e.id)}
                                                    onChange={() => basculer(e.id)}
                                                />
                                                {/* La MÊME pastille que le document imprime.
                                                    La maquette dessine ici un carré portant
                                                    l'icône de la catégorie ; un écran qui
                                                    promet une icône que le papier n'imprime
                                                    pas est un écran qui ment (écart E28). */}
                                                <span className="pl-dot" style={{ background: e.couleur }} />
                                                <span className="pl-exp-evt-lbl">{e.libelle}</span>
                                                <span className="pl-hint">
                                                    {e.date.slice(8, 10)}/{e.date.slice(5, 7)}
                                                </span>
                                            </label>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="pl-hint">
                                        Aucun événement placé sur le calendrier de cette année.
                                        Posez-en un en cliquant sur un numéro de jour, en mode édition.
                                    </div>
                                )}

                                <div className="pl-hint" style={{ marginTop: 10 }}>
                                    Les deux logos, le titre et « Généré le… » sont ajoutés automatiquement.
                                </div>
                            </div>
                        </div>

                        {/* ══ MODÈLES ENREGISTRÉS ═══════════════════════════ */}
                        <div className="pl-card">
                            <div className="pl-card-body pl-exp-docs">
                                <div className="pl-exp-sec" style={{ marginTop: 0 }}>
                                    <i className="fa-solid fa-server" /> Modèles d'export enregistrés
                                </div>

                                {modeles.map(m => (
                                    <div
                                        key={m.id}
                                        className={'pl-exp-doc' + (modeleActif === m.id ? ' actif' : '')}
                                    >
                                        <i className="fa-solid fa-sliders" />
                                        <div className="pl-ed-meta">
                                            <b>{m.titre}</b>
                                            <span className="pl-hint">
                                                {dateCourte(m.updated_at || m.created_at)}
                                                {m.auteur ? ' · ' + m.auteur : ''}
                                            </span>
                                            {m.obsolete && (
                                                <span className="pl-ed-stale" title="Le PDF que vous obtiendrez ne sera pas celui d'alors">
                                                    <i className="fa-solid fa-triangle-exclamation" /> événements modifiés depuis
                                                </span>
                                            )}
                                        </div>
                                        <button className="pl-ed-act" title="Charger ces réglages dans l'écran" onClick={() => appliquerModele(m)}>
                                            <i className="fa-solid fa-eye" />
                                        </button>
                                        <button className="pl-ed-act" title="Télécharger le PDF de ce modèle" onClick={() => telechargerModele(m)}>
                                            <i className="fa-solid fa-download" />
                                        </button>
                                        <button
                                            className={'pl-ed-act' + (m.surAccueil ? ' accueil-on' : '')}
                                            title={!peutEcrire ? motifLectureSeule
                                                : (m.surAccueil ? 'Vignette de l\'accueil — cliquer pour l\'en retirer' : 'Mettre ce modèle sur l\'accueil du module')}
                                            disabled={!peutEcrire}
                                            onClick={() => basculerAccueilModele(m)}
                                        >
                                            <i className="fa-solid fa-thumbtack" />
                                        </button>
                                        <button
                                            className="pl-ed-act"
                                            title={peutEcrire ? 'Envoyer le PDF de ce modèle par courriel (un message par personne)' : motifLectureSeule}
                                            disabled={!peutEcrire}
                                            onClick={() => { appliquerModele(m); setEnvoi(m); }}
                                        >
                                            <i className="fa-solid fa-paper-plane" />
                                        </button>
                                        <button
                                            className="pl-ed-act"
                                            title={peutEcrire ? 'Remplacer par les réglages actuels' : motifLectureSeule}
                                            disabled={!peutEcrire}
                                            onClick={() => demanderRemplacement(m)}
                                        >
                                            <i className="fa-solid fa-rotate" />
                                        </button>
                                        <button
                                            className="pl-ed-act danger"
                                            title={peutEcrire ? 'Supprimer ce modèle' : motifLectureSeule}
                                            disabled={!peutEcrire}
                                            onClick={() => demanderSuppression(m)}
                                        >
                                            <i className="fa-solid fa-trash" />
                                        </button>
                                    </div>
                                ))}

                                {!modeles.length && (
                                    <div className="pl-hint pl-exp-docs-vide">
                                        Aucun modèle enregistré. « Enregistrer le modèle » garde les réglages
                                        affichés sous un nom, pour tout le département.
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* La règle est dite à l'écran, une fois, là où elle s'applique. */}
                        <div className="pl-note">
                            <i className="fa-solid fa-circle-info" />
                            <span>
                                Un modèle garde <b>les réglages</b>, pas le fichier : télécharger
                                <b> refabrique</b> le PDF avec les événements d'aujourd'hui. Le document
                                obtenu est donc toujours à jour — mais ce n'est pas, à l'identique,
                                celui que vous aviez diffusé.
                            </span>
                        </div>
                    </div>

                    {/* ══ APERÇU ════════════════════════════════════════════ */}
                    <div className="pl-exp-right">
                        <div className="pl-card" style={{ marginBottom: 0 }}>
                            <div className="pl-card-header">
                                <span className="ttl"><i className="fa-solid fa-eye" /> Aperçu — le PDF tel qu'il sera</span>
                                <span className="pl-exp-zoom">
                                    <button className="pl-btn pl-btn-sm" onClick={() => setZoom(z => Math.max(20, (z || ajustement) - 15))} title="Dézoomer">
                                        <i className="fa-solid fa-magnifying-glass-minus" />
                                    </button>
                                    <span className="pl-exp-zlbl">
                                        {zoomEffectif}%{zoomEffectif === ajustement && <> ajusté</>}
                                    </span>
                                    <button className="pl-btn pl-btn-sm" onClick={() => setZoom(z => Math.min(400, (z || ajustement) + 15))} title="Zoomer">
                                        <i className="fa-solid fa-magnifying-glass-plus" />
                                    </button>
                                    <button className="pl-btn pl-btn-sm" onClick={() => setZoom(ajustement)} title="Ajuster la page au cadre">
                                        <i className="fa-solid fa-arrows-to-dot" />
                                    </button>
                                </span>
                            </div>
                            <div className="pl-card-body">
                                <div className="pl-exp-preview-wrap" ref={cadreRef}>
                                    {apercu ? (
                                        /* ⚠️ LA CLÉ PORTE LE ZOOM, ET C'EST LA SEULE FAÇON
                                           DONT CES BOUTONS PUISSENT MARCHER. Changer le seul
                                           FRAGMENT de `src` (`#zoom=…`) est une navigation
                                           « dans le même document » : le navigateur ne
                                           recharge rien et la visionneuse ne relit jamais son
                                           paramètre. Les boutons +/− du lot 5.6 étaient
                                           inopérants pour cette raison exacte ; la clé force
                                           React à REMONTER le cadre, donc à recharger le
                                           document, qui lit alors son zoom (écart E21). */
                                        <iframe
                                            key={apercu.url + '@' + zoomEffectif}
                                            className="pl-exp-preview"
                                            title="Aperçu du PDF"
                                            src={apercu.url + '#toolbar=0&navpanes=0&zoom=' + zoomEffectif}
                                        />
                                    ) : (
                                        <div className="pl-exp-vide"><i className="fa-solid fa-spinner fa-spin" /> Composition du PDF…</div>
                                    )}
                                </div>
                                <div className="pl-exp-note">
                                    {apercu && (
                                        <>
                                            A4 {orientation} · {orientation === 'paysage' ? 2 : 1} colonne
                                            {orientation === 'paysage' ? 's' : ''}
                                            {' '}· {apercu.evenements} événement{apercu.evenements > 1 ? 's' : ''}
                                            {grouperParMois ? ' groupés par mois' : ''}
                                            {' '}· <b>{apercu.pages} page{apercu.pages > 1 ? 's' : ''}</b>
                                            {' '}(pagination automatique)
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                )}
            </div>

            {/* ══ NOMMER UN MODÈLE ═════════════════════════════════════════
                Une saisie de texte, donc une modale — la charte du module interdit
                `window.prompt`, et c'est ce qui reste du dernier retiré au lot 5.2. */}
            {/* Lot PA.3 — « Envoyer » : le PDF de CE modèle, refait au moment de lancer, joint à chaque message */}
            {envoi && (
                <PlanningAnnuelEnvoi
                    departementId={deptId}
                    modele={envoi}
                    fabriquerPdf={() => construirePdf(reglagesDepuisConfig(envoi.config)).pdf}
                    modifiable={peutEcrire}
                    onFermer={() => setEnvoi(null)}
                />
            )}

            {nouveauModele && (
                <ModalePlanning
                    titre="Enregistrer ce modèle"
                    icone="fa-floppy-disk"
                    onFermer={() => setNouveauModele(null)}
                    pied={
                        <>
                            <button className="pl-btn" onClick={() => setNouveauModele(null)}>Annuler</button>
                            <button
                                className="pl-btn pl-btn-primary"
                                onClick={() => enregistrerModele(nouveauModele.nom.trim())}
                                disabled={enregistrement || !nouveauModele.nom.trim()}
                            >
                                <i className="fa-solid fa-floppy-disk" /> Enregistrer
                            </button>
                        </>
                    }
                >
                    <p>
                        Le modèle garde le titre, l'orientation, le regroupement par mois et les
                        événements retenus. Il est <b>partagé avec le département</b>.
                    </p>
                    <input
                        className="pl-f pl-exp-titre"
                        value={nouveauModele.nom}
                        autoFocus
                        maxLength={250}
                        placeholder="Nom du modèle — par exemple « Dates clés BUT 1 »"
                        onChange={e => { const nom = e.target.value; setNouveauModele(n => ({ ...n, nom })); }}
                        onKeyDown={e => {
                            if (e.key === 'Enter' && nouveauModele.nom.trim() && !enregistrement) {
                                enregistrerModele(nouveauModele.nom.trim());
                            }
                        }}
                    />
                    {/* v94 (lot PA.2) : le modèle devient une vignette de l'accueil du module */}
                    <label className="pl-exp-surAccueil">
                        <input
                            type="checkbox"
                            checked={nouveauModele.surAccueil === true}
                            onChange={e => { const surAccueil = e.target.checked; setNouveauModele(n => ({ ...n, surAccueil })); }}
                        />
                        Mettre ce modèle <b>sur l'accueil</b> du Planning annuel (une vignette : Télécharger le PDF en un geste)
                    </label>
                </ModalePlanning>
            )}

            {confirmation && (
                <ModalePlanning
                    titre={confirmation.titre}
                    icone={confirmation.icone}
                    onFermer={() => setConfirmation(null)}
                    pied={
                        <>
                            <button className="pl-btn" onClick={() => setConfirmation(null)}>Annuler</button>
                            <button
                                className={'pl-btn ' + (confirmation.danger ? 'pl-btn-danger' : 'pl-btn-primary')}
                                onClick={confirmation.action}
                                disabled={enregistrement}
                            >
                                {confirmation.libelle}
                            </button>
                        </>
                    }
                >
                    <p>{confirmation.message}</p>
                </ModalePlanning>
            )}

            {information && (
                <ModalePlanning
                    titre={information.titre}
                    icone="fa-circle-info"
                    onFermer={() => setInformation(null)}
                    pied={<button className="pl-btn pl-btn-primary" onClick={() => setInformation(null)}>Fermer</button>}
                >
                    <p>{information.message}</p>
                </ModalePlanning>
            )}
        </div>
    );
}

export default PlanningExportEvenements;
